'use client';

import React, { useEffect, useMemo } from 'react';
import { Button, Typography } from 'src/components';
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { useParams } from 'next/navigation';
import { useLayoutControl } from 'src/context/layout';
import { useNotification } from 'src/context/notification';
import { useCRMButtonContext } from 'src/context/providers/PushToCRMButtonProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useLogging } from 'src/context/logging';
import { useGetMappingsQuery, useGetUserInputOptionsQuery, usePushToCRMMutation } from 'src/api/redux';
import { entityTypeSlugMap } from 'src/components/Settings/constants';

interface CRMPushButtonProps {
  variant?: 'primary' | 'secondary';
  entityType?: string;
  mappingTypes?: string[];
  disabled?: boolean;
  className?: string;
}

const CRMPushButton: React.FC<CRMPushButtonProps> = ({
  variant = 'primary',
  entityType,
  mappingTypes = [],
  disabled: externalDisabled,
  className = '',
}) => {
  // Always call hooks at the top level, regardless of variant
  const { type } = useParams<{ type: string }>();
  const log = useLogging();
  const { clickedButtons, setClickedButtons, reenableButton } =
    useCRMButtonContext();
  const { entityDetails } = useProfile();
  const { provider_id, type: content_type, crm_links } = entityDetails || {};
  const { setNotification } = useNotification();
  const { setPushDuplicateToCRM, setPushProviderToCRM } = useLayoutControl();

  const normalizedType = type?.toLowerCase();
  const contentTypeValue = entityType
    ? entityType.toLowerCase()
    : normalizedType && entityTypeSlugMap[normalizedType]
    ? entityTypeSlugMap[normalizedType]
    : content_type?.toLowerCase();

  const [pushToCRM, { isLoading }] = usePushToCRMMutation();

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  const isThirdParty =
    allMappings?.results?.[0]?.crm?.toLowerCase() === 'third_party';

  const enabledCrmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  const disabledCrmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => !mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  const mappings = useMemo(() => {
    return allMappings?.results?.filter(
      ({ medscout_object_type, enabled }) =>
        medscout_object_type?.toLowerCase() === contentTypeValue && enabled
    );
  }, [allMappings, contentTypeValue]);

  const singleMappingId = mappings?.length === 1 ? String(mappings[0]?.id) : null;
  const { data: options } = useGetUserInputOptionsQuery(
    {
      mappingId: singleMappingId,
      page: 0,
      pageSize: 100,
    },
    { skip: !singleMappingId }
  );

  const isDisabled = externalDisabled || clickedButtons.includes(provider_id);

  const pushToCrmHandler = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (isDisabled) return;
    if (!mappings?.length || !provider_id) return;

    // Determine if all options are hidden
    const isAllHidden = options?.results?.every(
      (option) => option?.hidden === true
    );

    // Case: Only one mapping and all options are hidden
    if (mappings.length === 1 && (!options?.results?.length || isAllHidden)) {
      // There could be options, but they are all hidden
      const fieldsToPush = options?.results?.reduce((acc, option) => {
        acc[option?.crm_api_code] = option?.extra?.default_value || '';
        return acc;
      }, {});

      try {
        const result = await pushToCRM({
          object_id: provider_id,
          object_type: mappings[0]?.medscout_object_type,
          mapping: mappings[0]?.id,
          fields: fieldsToPush,
        });

        if ('data' in result && result.data?.duplicate) {
          const duplicateData = {
            mapping: mappings[0]?.id,
            object_type: mappings[0]?.medscout_object_type,
            object_id: provider_id,
            ...result.data,
          };
          setPushDuplicateToCRM(duplicateData);
          return;
        }

        setNotification({
          title: isThirdParty
            ? 'Push to CRM Initiated'
            : 'Push to CRM successful',
          message: isThirdParty
            ? 'Successfully initiated push to CRM. This may take a few minutes.'
            : 'Successfully pushed to CRM.',
          type: 'success',
        });
        if (isThirdParty) {
          setClickedButtons(provider_id);
        }
        log.event('Push to CRM', {
          ...entityDetails,
        });
      } catch (err: any) {
        setNotification({
          title: 'Push to CRM failed',
          message:
            err?.error_message ||
            'Error pushing to CRM, please try again later.',
          type: 'error',
        });
        log.exception(`Error pushing to CRM: ${err}`);
      }
    } else {
      // Case: Multiple mappings or visible options available
      setPushProviderToCRM(entityDetails, enabledCrmTypes[0]);
    }
  };

  useEffect(() => {
    const crmLinks =
      crm_links?.filter((link) => {
        const linkCrm = link.crm?.toLowerCase();
        // Show links from any CRM type that has a mapping (enabled or disabled)
        return (
          enabledCrmTypes.some((t) => t === linkCrm) ||
          disabledCrmTypes.some((t) => t === linkCrm)
        );
      }) || [];

    const count = crmLinks.length;

    if (count) {
      reenableButton(provider_id);
    }
  }, [
    variant,
    provider_id,
    crm_links,
    disabledCrmTypes,
    enabledCrmTypes,
    reenableButton,
  ]);

  return variant === 'secondary' ? (
    <Button
      variant="solid"
      size="md"
      className={`rounded-full w-full bg-sky-50 hover:bg-sky-100 active:bg-sky-200 ${className}`}
      color="primary"
      onClick={pushToCrmHandler}
      disabled={isDisabled}
    >
      <div className="flex items-center gap-2">
        <Icon
          icon={faCloudArrowUp}
          height={20}
          width={20}
          className="text-neutral-600"
        />
        <Typography className="text-primary text-base font-semibold">
          {mappingTypes.length === 1
            ? `Push as ${mappingTypes[0]}`
            : 'Push to New'}
        </Typography>
      </div>
    </Button>
  ) : (
    <Button
      variant="solid"
      size="md"
      className={`rounded-full max-w-96 min-w-80 bg-sky-600 hover:bg-sky-800 active:bg-sky-900 ${className}`}
      color="primary"
      onClick={pushToCrmHandler}
      disabled={isDisabled || isLoading}
    >
      <div className="flex items-center gap-2">
        <Icon
          icon={faCloudArrowUp}
          height={20}
          width={20}
          className="text-white"
        />
        <Typography className="text-white text-base font-semibold">
          Push to CRM
        </Typography>
      </div>
    </Button>
  );
};

export default CRMPushButton;
